<template>
  <div class="d-flex mt-5">
    <div class="mr-3">
      <RangeGrid
        :partition="gridPartition"
        :partitionExcludingBoard="gridPartitionExcludingBoardCards"
        :colorScheme="colorScheme"
        @toggleSquare="toggleSquare"
      />
      <Board v-model="board" class="mt-3" />
    </div>
    <div>
      <SubrangeList
        :subranges="subrangeDefinitions"
        :colorScheme="colorScheme"
        :activeSubrangeIndex="activeSubrangeIndex"
        :comboCounts="comboCounts"
        @activateSubrange="activateSubrange"
        class="mb-3"
      />
      <SuitSelect
        v-model="selectedSuitComboStrings"
        :subranges="subrangeDefinitions"
        :board="board"
        :activeSubrangeIndex="activeSubrangeIndex"
        :alternateSubrangeIndex="alternateSubrangeIndex"
        @changeAlternateSubrangeIndex="setAlternateSubrangeIndex"
      />
    </div>
  </div>
</template>

<script>
import mapValues from "lodash/mapValues";
import qs from "query-string";
import RangeGrid from "./RangeGrid";
import {
  allSuitComboStrings,
  comboStringsToSuitPartition,
  doComboStringsMatchSuitComboStrings,
  excludeCardsFromComboStrings,
  getEmptyGridPartition,
  gridPartitionToComboStringsBySubrangeIndex,
  invertSuitComboStringSet,
  squareStringToComboStrings,
  stringToCards,
} from "../helpers/cards";
import SubrangeList from "./SubrangeList";
import Board from "./Board";
import { demoBoard, demoColorScheme, demoSubranges } from "../demoContent";
import SuitSelect from "./SuitSelect";

export default {
  name: "PageRangeAnalyzer",

  components: { SuitSelect, Board, SubrangeList, RangeGrid },

  data() {
    const subrangeDefinitions = demoSubranges;
    const lastSubrangeIndex = subrangeDefinitions.length - 1;
    const queryString = qs.parse(window.location.search);
    const alternateByActiveSubrangeIndex = subrangeDefinitions.map((data, i) => {
      if (i < subrangeDefinitions.length - 1) return subrangeDefinitions.length - 1;
      return subrangeDefinitions.length - 2;
    });
    return {
      gridPartition: getEmptyGridPartition(),
      subrangeDefinitions,
      activeSubrangeIndex: 0,
      alternateByActiveSubrangeIndex,
      selectedSuitComboStrings: [],
      board: queryString.board ? stringToCards(queryString.board) : demoBoard,
      squareToggleActivatesSquares: true,
    };
  },

  computed: {
    colorScheme() {
      return demoColorScheme;
    },
    alternateSubrangeIndex() {
      return this.alternateByActiveSubrangeIndex[this.activeSubrangeIndex];
    },
    activeSuitComboStrings() {
      // If no suits are selected, that means that all suits are active
      return this.selectedSuitComboStrings.length === 0
        ? allSuitComboStrings
        : this.selectedSuitComboStrings;
    },
    gridPartitionExcludingBoardCards() {
      return mapValues(this.gridPartition, data => {
        const comboStringsBySubrangeIndex = mapValues(
          data.comboStringsBySubrangeIndex,
          comboStrings => excludeCardsFromComboStrings(comboStrings, this.board)
        );
        return { ...data, comboStringsBySubrangeIndex };
      });
    },
    comboCounts() {
      const comboStringsBySubrangeIndex = gridPartitionToComboStringsBySubrangeIndex(
        this.gridPartitionExcludingBoardCards
      );
      const handsInRangeIgnoringBoard = [].concat(...Object.values(comboStringsBySubrangeIndex));
      return {
        total: handsInRangeIgnoringBoard.length,
        bySubrangeIndex: mapValues(
          comboStringsBySubrangeIndex,
          comboStrings => comboStrings.length
        ),
      };
    },
  },

  methods: {
    activateSubrange(index) {
      this.activeSubrangeIndex = index;
      if (this.alternateSubrangeIndex === index) {
        this.setAlternateSubrangeIndex((index + 1) % this.subrangeDefinitions.length);
      }
    },
    toggleSquare(squareString, isFirstSquare) {
      const squareData = this.gridPartition[squareString];
      const squareDataExcludingBoard = this.gridPartitionExcludingBoardCards[squareString];
      // If this is the start of a multi-toggle, check on the first square whether it's activating or deactivating.
      // It's only activating if the active suit selection exactly matches the square's suit split.
      if (isFirstSquare) {
        const activeComboStrings =
          squareDataExcludingBoard.comboStringsBySubrangeIndex[this.activeSubrangeIndex] || [];
        const availableComboStrings = excludeCardsFromComboStrings(
          squareStringToComboStrings(squareString),
          this.board
        );
        const activeCombosMatchSuitSelection = doComboStringsMatchSuitComboStrings(
          availableComboStrings,
          activeComboStrings,
          this.activeSuitComboStrings
        );
        const alternateComboStrings =
          squareDataExcludingBoard.comboStringsBySubrangeIndex[this.alternateSubrangeIndex] || [];
        const alternateCombosMatchSuitSelection = doComboStringsMatchSuitComboStrings(
          availableComboStrings,
          alternateComboStrings,
          invertSuitComboStringSet(this.activeSuitComboStrings)
        );
        this.squareToggleActivatesSquares =
          !activeCombosMatchSuitSelection || !alternateCombosMatchSuitSelection;
      }

      const allComboStrings = squareStringToComboStrings(squareString);
      const suitPartition = comboStringsToSuitPartition(
        allComboStrings,
        this.activeSuitComboStrings
      );

      if (this.squareToggleActivatesSquares) {
        squareData.comboStringsBySubrangeIndex = {
          [this.activeSubrangeIndex]: suitPartition.matching,
          [this.alternateSubrangeIndex]: suitPartition.notMatching,
        };
      } else {
        squareData.comboStringsBySubrangeIndex = {
          [this.activeSubrangeIndex]: [],
          [this.alternateSubrangeIndex]: [],
        };
      }
    },
    setAlternateSubrangeIndex(index) {
      this.$set(this.alternateByActiveSubrangeIndex, this.activeSubrangeIndex, index);
    },
  },
};
</script>

<style scoped lang="scss"></style>
